import { Vector3 } from 'three';
import type { System } from '@/core/system';
import type { Engine } from '@/core/engine';
import type { Time } from '@/core/time';
import { City, CellKind, type Building, type OrnamentKind } from '@/world/city';
import { CityGenerator } from '@/generation/city-generator';
import { Ground } from '@/rendering/ground';
import { BuildingField } from '@/rendering/building-field';
import { SkyLinkField } from '@/rendering/skylink-field';
import { PlayerAnalyzer } from './player-analyzer';
import { AI, WORLD } from '@/config';
import { clamp, clamp01, damp } from '@/utils/math';
import { RNG } from '@/utils/rng';
import { dominantPreference } from '@/core/types';
import type { PreferenceKey, PreferenceVector } from '@/core/types';

/**
 * The City Evolution Engine — the mind of the city.
 *
 * On a slow cadence it appraises a menu of possible self-modifications, scores each
 * by how well it serves the player's inferred preferences (a Utility-AI arbitration
 * with softmax selection so the city has taste, not determinism), and enacts one.
 * Crucially every action is INCREMENTAL and ANIMATED: the city grows, it never
 * reloads. The player should think "did something change back there?", never "the
 * level swapped."
 *
 * The engine owns no rendering. It mutates the City model and pokes the render
 * fields to re-derive; the growth animations live in those fields.
 */

/** Above this many ornaments the city stops planting more set-dressing so the
 *  render fields never overflow and the scene never turns to clutter. */
const ORNAMENT_CAP = 700;

/** Actions that plant a lot of set-dressing; suppressed as the cap approaches. */
const ORNAMENT_HEAVY = new Set<string>([
  'narrow-alleys',
  'open-boulevard',
  'raise-overlook',
  'plant-garden',
  'weave-parkour',
]);

interface EvolveContext {
  city: City;
  gen: CityGenerator;
  ground: Ground;
  buildings: BuildingField;
  links: SkyLinkField;
  player: Vector3;
  prefs: PreferenceVector;
  activity: number;
  rng: RNG;
  /** Where the change happened, in world space. Set by the enacting action so the
   *  ground ripple and the emitted event can be spatialized. */
  epicenter: { x: number; z: number } | null;
}

interface EvolutionAction {
  id: string;
  driver: PreferenceKey;
  /** Base utility from preferences & city state (0 = never). */
  score(ctx: EvolveContext): number;
  /** Enact the change. Returns a human-readable description, or null if it no-oped. */
  apply(ctx: EvolveContext): string | null;
}

export class CityEvolutionEngine implements System {
  readonly name = 'evolution';

  /** The FinaleDirector pauses evolution during the ceremony so the city holds
   *  still while it remembers; released afterwards for the open-ended epilogue. */
  paused = false;

  /** Seconds the player has actually spent in the world this session. */
  private playTime = 0;
  private timer = 0;
  private prefs: PreferenceVector;
  private activity = 0.5;
  private rng: RNG;
  private lastApplied = new Map<string, number>();
  private actions: EvolutionAction[];

  constructor(
    private engine: Engine,
    private city: City,
    private gen: CityGenerator,
    private ground: Ground,
    private buildings: BuildingField,
    private links: SkyLinkField,
    private analyzer: PlayerAnalyzer,
    private playerPos: () => Vector3,
  ) {
    this.rng = city.rng.fork(777);
    this.prefs = analyzer.getPreferences();
    this.actions = this.buildActions();
  }

  init(): void {
    this.engine.events.on('ai:preferences', (p) => {
      this.prefs = p;
    });
  }

  update(time: Time): void {
    // The city sleeps until the player arrives, and holds still while it remembers.
    if (!this.engine.hasEntered || this.paused) return;
    this.timer += time.delta;

    // Time floor: however the player moves (or doesn't), the city's maturity can
    // never lag behind the time they've spent here, so the piece always reaches its
    // ending — engaged play simply gets there sooner than this ceiling.
    this.playTime += time.delta;
    const floor = clamp01(this.playTime / AI.finaleGuaranteeSeconds);
    if (this.city.maturity < floor) this.city.maturity = floor;

    const snap = this.analyzer.getSnapshot();
    if (snap) {
      // Vitality tracks how alive the player's behavior is. Stagnation drains it.
      const target = clamp01(0.25 + snap.activity * 0.55 + clamp01(snap.noveltyRate * 1.2) * 0.3);
      this.city.vitality = damp(this.city.vitality, target, 0.15, time.delta);
      this.activity = snap.activity;
      // Living in the city — moving, exploring — drips maturity toward the finale,
      // so engaged play is rewarded with visible progress.
      this.city.maturity = clamp01(
        this.city.maturity + snap.activity * AI.maturityActivityDrip * time.delta,
      );
    }

    if (this.timer < AI.evolveInterval) return;
    this.timer = 0;
    this.decide();
  }

  private ctx(): EvolveContext {
    return {
      city: this.city,
      gen: this.gen,
      ground: this.ground,
      buildings: this.buildings,
      links: this.links,
      player: this.playerPos(),
      prefs: this.prefs,
      activity: this.activity,
      rng: this.rng,
      epicenter: null,
    };
  }

  private decide(): void {
    const ctx = this.ctx();
    // When the world is nearly saturated with set-dressing, steer away from the
    // actions that plant a lot of it so the city keeps changing without cluttering.
    const nearCap = this.city.ornaments.length >= ORNAMENT_CAP * 0.85;
    // The single thing the player is most clearly doing right now.
    const dom = dominantPreference(this.prefs);
    // Score every action; apply a cooldown penalty so the city doesn't fixate.
    const scored = this.actions.map((a) => {
      const cd = this.city.maturity - (this.lastApplied.get(a.id) ?? -1);
      const cooldown = clamp01(cd / 0.04); // recently used actions are suppressed
      const capBias = nearCap && ORNAMENT_HEAVY.has(a.id) ? 0.25 : 1;
      let s = Math.max(0, a.score(ctx)) * (0.5 + 0.5 * cooldown) * capBias;
      // Strongly favour the action that answers what you're most clearly doing, so
      // the city's response reads as a direct reply to how you move — not noise.
      if (a.driver === dom.key && dom.value > 0.2) s *= 2.6;
      // A player who craves height (looks up) also needs a way UP, so the climbing
      // stair rides alongside the taller skyline rather than leaving them stranded.
      if (dom.key === 'verticality' && a.id === 'weave-parkour' && dom.value > 0.2) s *= 2.2;
      return { a, s };
    });

    // Softmax-ish weighted pick — the city usually follows its strongest read of
    // you, but occasionally surprises, which keeps the world from feeling scripted.
    //
    // Crucially this is a weighted draw WITHOUT replacement, tried in order until an
    // action actually succeeds. An action can legitimately be impossible (the
    // frontier is at its maximum radius, no plaza is left to plant a garden in…); if
    // the city simply gave up on a no-op, the dominant action would be re-chosen
    // every tick forever and the whole evolution — and the finale — would stall.
    const pool = scored.filter((x) => x.s > 1e-4);
    let chosen: { a: EvolutionAction; s: number } | null = null;
    let desc: string | null = null;
    while (pool.length) {
      const total = pool.reduce((sum, x) => sum + x.s, 0);
      let r = this.rng.next() * total;
      let idx = pool.length - 1;
      for (let i = 0; i < pool.length; i++) {
        r -= pool[i].s;
        if (r <= 0) {
          idx = i;
          break;
        }
      }
      const cand = pool.splice(idx, 1)[0];
      ctx.epicenter = null;
      const d = cand.a.apply(ctx);
      if (d) {
        chosen = cand;
        desc = d;
        break;
      }
    }
    if (!chosen || !desc) return;

    // Every change ripples the floor at its locus — a quiet "something happened
    // here" the player can read even from across the district.
    const ep = ctx.epicenter;
    if (ep) this.ground.ripple(ep.x, ep.z);

    this.lastApplied.set(chosen.a.id, this.city.maturity);
    this.city.maturity = clamp01(this.city.maturity + AI.maturityPerAction);
    this.engine.events.emit('city:evolved', {
      action: desc,
      driver: chosen.a.driver,
      maturity: this.city.maturity,
      epicenter: ep ?? undefined,
    });
  }

  /* ------------------------------------------------------------------ */
  /* The action menu                                                     */
  /* ------------------------------------------------------------------ */

  private buildActions(): EvolutionAction[] {
    return [
      /* --- VERTICALITY: raise a district, crown its tallest roof with a beacon --- */
      {
        id: 'raise-district',
        driver: 'verticality',
        score: (c) => 0.06 + c.prefs.verticality * 1.8,
        apply: (c) => {
          const target = this.pickBuiltBuilding(c, true);
          if (!target) return null;
          let region = this.buildingsAround(c, target, 2, 10);
          if (!region.length) region = [target]; // fallback: at least raise the seed
          const boost = c.rng.range(1.25, 1.7);
          let tallest = region[0];
          for (const b of region) {
            b.height = clamp(b.height * boost + c.rng.range(1, 4), 4, 60);
            if (b.height > tallest.height) tallest = b;
          }
          // A beacon on the highest new roof marks where the city grew.
          this.addOrn(c, 'beacon', tallest.x, tallest.height, tallest.z, 0, c.rng.range(0.9, 1.15));
          c.epicenter = { x: tallest.x, z: tallest.z };
          return 'a district reaches upward';
        },
      },

      /* --- PARKOUR: a CLIMBABLE stair of platforms, starting beside the player --- */
      {
        id: 'weave-parkour',
        driver: 'parkour',
        score: (c) => c.prefs.parkour * 1.9 + c.prefs.verticality * 0.4,
        apply: (c) => {
          // A gentle ascending stair rooted next to the player: the first platform
          // is a hop off the ground and each step rises < the jump apex, so this is
          // an actual way UP — not decoration floating out of reach.
          const px = c.player.x;
          const pz = c.player.z;
          const ang = c.rng.range(0, Math.PI * 2);
          const dx = Math.cos(ang);
          const dz = Math.sin(ang);
          const count = c.rng.int(4, 6);
          const gap = c.rng.range(3.8, 4.6); // horizontal spacing between steps
          const rise = c.rng.range(1.4, 1.6); // vertical step, below the ~1.9m apex
          let y = c.rng.range(1.3, 1.6); // first step is reachable from the ground
          let x = px + dx * (gap * 0.7);
          let z = pz + dz * (gap * 0.7);
          for (let i = 0; i < count; i++) {
            const w = c.rng.range(3.2, 4.0); // wide, forgiving landings
            c.city.addLink({ ax: x, ay: y, az: z, bx: x, by: y, bz: z, width: w, kind: 'platform' });
            this.addOrn(c, 'lantern', x + dx * w * 0.5, y + 1.1, z + dz * w * 0.5, 0, 0.9);
            x += dx * gap;
            z += dz * gap;
            y += rise;
          }
          c.epicenter = { x: px, z: pz };
          return 'a stair of platforms climbs into the air';
        },
      },

      /* --- VERTICALITY + PARKOUR: bridge two rooftops, lanterns down the span --- */
      {
        id: 'skybridge',
        driver: 'verticality',
        score: (c) => (c.prefs.verticality + c.prefs.parkour) * 0.8,
        apply: (c) => {
          const tall = c.city.tallest(14).filter((b) => b.height > 8);
          const a = tall.length ? c.rng.pick(tall) : null;
          let best: Building | null = null;
          let bestD = Infinity;
          if (a) {
            for (const b of tall) {
              if (b === a) continue;
              const d = Math.hypot(b.x - a.x, b.z - a.z);
              if (d > 8 && d < 34 && d < bestD) {
                bestD = d;
                best = b;
              }
            }
          }
          if (!a || !best) {
            // Fallback: no good pair — nudge the single tallest a little higher so
            // the action still reads instead of no-oping.
            const solo = c.city.tallest(1)[0];
            if (!solo) return null;
            solo.height = clamp(solo.height + c.rng.range(3, 6), 4, 60);
            this.addOrn(c, 'beacon', solo.x, solo.height, solo.z, 0, 1);
            c.epicenter = { x: solo.x, z: solo.z };
            return 'a rooftop stretches toward the sky';
          }
          const y = Math.min(a.height, best.height) - 0.4;
          c.city.addLink({ ax: a.x, ay: y, az: a.z, bx: best.x, by: y, bz: best.z, width: 2.6, kind: 'bridge' });
          // 3–5 lanterns evenly spaced along the span light the crossing.
          const lamps = c.rng.int(3, 5);
          for (let i = 1; i <= lamps; i++) {
            const t = i / (lamps + 1);
            this.addOrn(c, 'lantern', a.x + (best.x - a.x) * t, y + 1.1, a.z + (best.z - a.z) * t, 0, 0.85);
          }
          c.epicenter = { x: (a.x + best.x) * 0.5, z: (a.z + best.z) * 0.5 };
          return 'a bridge spans the rooftops';
        },
      },

      /* --- INTIMACY: tighten streets into alleys, string lanterns + a gate --- */
      {
        id: 'narrow-alleys',
        driver: 'intimacy',
        score: (c) => c.prefs.intimacy * 1.6,
        apply: (c) => {
          const seed = this.pickBuiltBuilding(c, true);
          if (!seed) return null;
          const region = this.buildingsAround(c, seed, 2, 12);
          let changed = 0;
          const cs = c.city.cellSize;
          for (const b of region) {
            const nw = clamp(b.width * c.rng.range(1.08, 1.22), 2, cs * 0.92);
            const nd = clamp(b.depth * c.rng.range(1.08, 1.22), 2, cs * 0.92);
            if (nw !== b.width || nd !== b.depth) {
              b.width = nw;
              b.depth = nd;
              c.buildings.refresh(b);
              changed++;
            }
          }
          // The alley runs along one axis from the seed; hang a string of lanterns
          // down it and plant a gate at its mouth so the threshold is unmistakable.
          const along = c.rng.chance(0.5);
          const dir = c.rng.chance(0.5) ? 1 : -1;
          const lamps = c.rng.int(4, 8);
          for (let i = 1; i <= lamps; i++) {
            const off = i * (cs * 0.5) * dir;
            const lx = seed.x + (along ? off : 0);
            const lz = seed.z + (along ? 0 : off);
            this.addOrn(c, 'lantern', lx, c.rng.range(3.2, 4.2), lz, 0, c.rng.range(0.75, 0.95));
          }
          const gx = seed.x + (along ? -dir * cs * 0.5 : 0);
          const gz = seed.z + (along ? 0 : -dir * cs * 0.5);
          this.addOrn(c, 'gate', gx, 0, gz, along ? Math.PI / 2 : 0, c.rng.range(0.85, 1.05));
          const plaza = this.pickCellOfKind(c, CellKind.Plaza, seed.cx, seed.cz, 2);
          if (plaza) this.spawnInfill(c, plaza.cx, plaza.cz);
          c.epicenter = { x: seed.x, z: seed.z };
          return changed || lamps ? 'the streets tighten into alleys' : null;
        },
      },

      /* --- VELOCITY: carve a boulevard, paired gates + runway beacons --- */
      {
        id: 'open-boulevard',
        driver: 'velocity',
        score: (c) => c.prefs.velocity * 1.4,
        apply: (c) => {
          const seed = this.pickBuiltBuilding(c, true);
          if (!seed) return null;
          const horizontal = c.rng.chance(0.5);
          const region = c.city.buildings.filter((b) => {
            if (b.dying) return false;
            return horizontal
              ? b.cz === seed.cz && Math.abs(b.cx - seed.cx) <= 5
              : b.cx === seed.cx && Math.abs(b.cz - seed.cz) <= 5;
          });
          let cleared = 0;
          for (const b of region) {
            b.dying = true;
            c.city.setKind(b.cx, b.cz, CellKind.Plaza);
            cleared++;
          }
          if (cleared) c.ground.syncFromCity(c.city);
          // Dress the run: paired gates marching down the length, beacons on the
          // edges like landing lights.
          const cs = c.city.cellSize;
          const half = cs * 0.42;
          for (let s = -4; s <= 4; s += 2) {
            const mx = seed.x + (horizontal ? s * cs : 0);
            const mz = seed.z + (horizontal ? 0 : s * cs);
            // Gates straddle the boulevard, rotated to face along it.
            this.addOrn(c, 'gate', mx, 0, mz, horizontal ? 0 : Math.PI / 2, c.rng.range(0.9, 1.1));
            // Edge beacons run parallel on both sides.
            const ex = horizontal ? mx : mx + (s % 4 === 0 ? half : -half);
            const ez = horizontal ? mz + (s % 4 === 0 ? half : -half) : mz;
            this.addOrn(c, 'beacon', ex, 0, ez, 0, 0.8);
          }
          c.epicenter = { x: seed.x, z: seed.z };
          return 'a boulevard opens for the running';
        },
      },

      /* --- CONTEMPLATION (landmark): raise an overlook ringed by a small grove --- */
      {
        id: 'raise-overlook',
        driver: 'contemplation',
        score: (c) => c.prefs.contemplation * 1.4,
        apply: (c) => {
          const cell = this.pickCellOfKind(c, CellKind.Plaza, undefined, undefined, 0)
            ?? this.frontierPlazaNear(c);
          if (!cell) return null;
          const w = c.city.cellToWorld(cell.cx, cell.cz);
          const maxH = c.city.tallest(1)[0]?.height ?? 12;
          const b = c.city.addBuilding({
            x: w.x, z: w.z,
            width: c.city.cellSize * 0.34,
            depth: c.city.cellSize * 0.34,
            yaw: 0,
            height: clamp(maxH * 1.5 + 8, 18, 62),
            tone: 0.9,
            seed: c.rng.range(0, 1000),
            style: 'tower',
            cx: cell.cx, cz: cell.cz,
            bornAt: 0,
          });
          c.city.setKind(cell.cx, cell.cz, CellKind.Block);
          c.city.addLink({
            ax: b.x, ay: b.height + 0.2, az: b.z,
            bx: b.x, by: b.height + 0.2, bz: b.z,
            width: c.city.cellSize * 0.5,
            kind: 'platform',
          });
          // A ring of trees and stones nestles at the base — somewhere to stand and
          // look up from.
          const ring = c.rng.int(5, 7);
          const rad = c.city.cellSize * 0.42;
          for (let i = 0; i < ring; i++) {
            const a = (i / ring) * Math.PI * 2 + c.rng.range(-0.15, 0.15);
            const kind = c.rng.chance(0.65) ? 'tree' : 'stone';
            this.addOrn(c, kind, b.x + Math.cos(a) * rad, 0, b.z + Math.sin(a) * rad, c.rng.range(0, Math.PI * 2), c.rng.range(0.8, 1.15));
          }
          c.epicenter = { x: b.x, z: b.z };
          return 'a quiet landmark rises to be seen';
        },
      },

      /* --- CONTEMPLATION (garden): convert a plaza into a still garden --- */
      {
        id: 'plant-garden',
        driver: 'contemplation',
        score: (c) => c.prefs.contemplation * 1.5,
        apply: (c) => {
          // Prefer a plaza the player is standing near; else any plaza / frontier.
          const near = c.city.worldToCell(c.player.x, c.player.z);
          const cell =
            this.pickCellOfKind(c, CellKind.Plaza, near.cx, near.cz, 3) ??
            this.pickCellOfKind(c, CellKind.Plaza, undefined, undefined, 0) ??
            this.frontierPlazaNear(c);
          if (!cell) return null;
          // Keep it a Plaza so the garden stays open and walkable.
          c.city.setKind(cell.cx, cell.cz, CellKind.Plaza);
          const w = c.city.cellToWorld(cell.cx, cell.cz);
          // A still pool at the centre.
          this.addOrn(c, 'pool', w.x, 0.02, w.z, 0, c.rng.range(0.9, 1.2));
          // A ring of trees, with a few stones tucked between them.
          const trees = c.rng.int(4, 7);
          const rad = c.city.cellSize * 0.32;
          for (let i = 0; i < trees; i++) {
            const a = (i / trees) * Math.PI * 2 + c.rng.range(-0.1, 0.1);
            this.addOrn(c, 'tree', w.x + Math.cos(a) * rad, 0, w.z + Math.sin(a) * rad, c.rng.range(0, Math.PI * 2), c.rng.range(0.85, 1.15));
          }
          const stones = c.rng.int(2, 4);
          for (let i = 0; i < stones; i++) {
            const a = c.rng.range(0, Math.PI * 2);
            const sr = c.city.cellSize * c.rng.range(0.14, 0.24);
            this.addOrn(c, 'stone', w.x + Math.cos(a) * sr, 0, w.z + Math.sin(a) * sr, c.rng.range(0, Math.PI * 2), c.rng.range(0.7, 1.05));
          }
          c.epicenter = { x: w.x, z: w.z };
          return 'a garden settles into the plaza';
        },
      },

      /* --- EXPLORATION: grow the frontier outward --- */
      {
        id: 'grow-frontier',
        driver: 'exploration',
        score: (c) => 0.12 + c.prefs.exploration * 1.7,
        apply: (c) => {
          if (c.city.builtRadius >= WORLD.maxRadius) return null;
          const added = c.gen.growFrontier(1);
          c.ground.syncFromCity(c.city);
          // Ripple out at the frontier in the direction the player faces/stands.
          const px = c.player.x;
          const pz = c.player.z;
          const len = Math.max(1, Math.hypot(px, pz));
          const reach = c.city.builtRadius * c.city.cellSize * 0.85;
          c.epicenter = { x: (px / len) * reach, z: (pz / len) * reach };
          return added > 0 ? 'new streets unfold at the edges' : 'the horizon shifts outward';
        },
      },

      /* --- STAGNATION: when the player stops living, the city dims and closes --- */
      {
        id: 'decay',
        driver: 'exploration',
        score: (c) => (1 - c.activity) * (1 - c.prefs.exploration) * 1.2 - 0.15,
        apply: (c) => {
          const ring = c.city.builtRadius;
          const candidates = c.city.buildingsInRing(ring - 1, ring);
          if (!candidates.length) return null;
          c.rng.shuffle(candidates);
          const n = Math.min(candidates.length, c.rng.int(1, 3));
          let ex = 0;
          let ez = 0;
          for (let i = 0; i < n; i++) {
            const b = candidates[i];
            b.dying = true;
            c.city.setKind(b.cx, b.cz, CellKind.Void);
            // Dissolve any set-dressing that stood there too, so nothing is left
            // floating over the erased ground.
            this.removeOrnamentsNear(c, b.x, b.z, c.city.cellSize * 0.9);
            ex += b.x;
            ez += b.z;
          }
          c.ground.syncFromCity(c.city);
          c.epicenter = { x: ex / n, z: ez / n };
          return 'the outskirts fade back into paper';
        },
      },
    ];
  }

  /* ------------------------------------------------------------------ */
  /* Enactment helpers                                                   */
  /* ------------------------------------------------------------------ */

  /**
   * Plant one ornament, honouring the global cap so the render fields never
   * overflow and the ink-wash restraint is preserved. Silently skips when full.
   */
  private addOrn(
    c: EvolveContext,
    kind: OrnamentKind,
    x: number,
    y: number,
    z: number,
    yaw = 0,
    scale = 1,
  ): void {
    if (c.city.ornaments.length >= ORNAMENT_CAP) return;
    c.city.addOrnament({ kind, x, y, z, yaw, scale, seed: c.rng.range(0, 1000) });
  }

  /**
   * Remove ornaments within `radius` of a point by splicing them out of the model
   * directly. The engine holds no reference to the OrnamentField (its constructor
   * is fixed), but the field reconciles orphaned slots each frame, so this reclaims
   * their render slots cleanly on the next tick.
   */
  private removeOrnamentsNear(c: EvolveContext, x: number, z: number, radius: number): void {
    const r2 = radius * radius;
    for (let i = c.city.ornaments.length - 1; i >= 0; i--) {
      const o = c.city.ornaments[i];
      const dx = o.x - x;
      const dz = o.z - z;
      if (dx * dx + dz * dz <= r2) c.city.ornaments.splice(i, 1);
    }
  }

  private pickBuiltBuilding(c: EvolveContext, nearPlayer: boolean): Building | null {
    const alive = c.city.buildings.filter((b) => !b.dying && b.slot >= 0);
    if (!alive.length) return null;
    if (!nearPlayer) return c.rng.pick(alive);
    // Strongly favour the nearest building so change clearly happens where the
    // player is — only a little jitter so it isn't always the exact same one.
    const px = c.player.x;
    const pz = c.player.z;
    let best: Building | null = null;
    let bestScore = -Infinity;
    for (let i = 0; i < 20; i++) {
      const b = c.rng.pick(alive);
      const d = Math.hypot(b.x - px, b.z - pz);
      const score = -d + c.rng.range(0, 8);
      if (score > bestScore) {
        bestScore = score;
        best = b;
      }
    }
    return best;
  }

  private buildingsAround(
    c: EvolveContext,
    centre: Building,
    cellRadius: number,
    max: number,
  ): Building[] {
    const region = c.city.buildings.filter(
      (b) =>
        !b.dying &&
        b.slot >= 0 &&
        Math.abs(b.cx - centre.cx) <= cellRadius &&
        Math.abs(b.cz - centre.cz) <= cellRadius,
    );
    return region.slice(0, max);
  }

  private pickCellOfKind(
    c: EvolveContext,
    kind: CellKind,
    nearCx?: number,
    nearCz?: number,
    radius = 3,
  ): { cx: number; cz: number } | null {
    const r = c.city.builtRadius;
    const centre = Math.round(c.city.centre);
    const found: { cx: number; cz: number }[] = [];
    for (let cz = centre - r; cz <= centre + r; cz++) {
      for (let cx = centre - r; cx <= centre + r; cx++) {
        if (c.city.kindAt(cx, cz) !== kind) continue;
        if (nearCx !== undefined && nearCz !== undefined && radius > 0) {
          if (Math.abs(cx - nearCx) > radius || Math.abs(cz - nearCz) > radius) continue;
        }
        found.push({ cx, cz });
      }
    }
    return found.length ? c.rng.pick(found) : null;
  }

  private frontierPlazaNear(c: EvolveContext): { cx: number; cz: number } | null {
    return this.pickCellOfKind(c, CellKind.Plaza);
  }

  private spawnInfill(c: EvolveContext, cx: number, cz: number): void {
    const w = c.city.cellToWorld(cx, cz);
    const cs = c.city.cellSize;
    c.city.addBuilding({
      x: w.x + c.rng.range(-2, 2),
      z: w.z + c.rng.range(-2, 2),
      width: cs * c.rng.range(0.32, 0.5),
      depth: cs * c.rng.range(0.32, 0.5),
      yaw: c.rng.range(-0.15, 0.15),
      height: c.rng.range(5, 11),
      tone: c.rng.next(),
      seed: c.rng.range(0, 1000),
      style: 'stack',
      cx, cz,
      bornAt: 0,
    });
    c.city.setKind(cx, cz, CellKind.Block);
    c.ground.syncFromCity(c.city);
  }
}
